import axios, {AxiosRequestConfig, AxiosResponse} from 'axios';
import {format} from 'date-fns';
import {
  Currency,
  PaymentMethod,
  LogUserActionRequest, LogUserActionResponse,
  RegisterRequestBody, RegisterResponse,
  LoginResponse,
  ListCustomersBody,
  ApiResponseFields,
  InvoiceOrderBody, InvoiceOrderResponse,
  GetCurrentMonthInvoices,
  ListCustomersByAgentIDResponse,
  ListCustomersResponse,
  CancelInvoiceBody, CancelInvoiceResponse,
  CurrentUserResponse,
  GetInvoicesListResponse,
  GetInvoicesListForExcelResponse,
  GetInvoiceDocumentResponse,
  GetReceiptsListResponse,
  GetReceiptsListForExcelResponse,
  GetReceiptDocumentResponse,
  GetInvoiceOrdersListResponse,
  GetInvoiceOrdersListForExcelResponse,
  GetInvoiceOrderDetailsResponse,
  GetPaymentPageResponse,
  CapturePaypalPaymentResponse,
  InviteFriendRequestBody, InviteFriendResponse,
  GetCustomerOptionsRequestBody, GetCustomerOptionsResponse,
  UserDetailsBody,
  DashboardDataResponse,
  MarkMessageAsReadBody, MarkMessageAsReadResponse,
  GetPaymentFormDetailsByInvoiceBody,GetPaymentFormDetailsByInvoiceResponse,
  GetPaymentFormDetailsByCustomerIDBody, GetPaymentFormDetailsByCustomerIDResponse,
  GetTransfersListResponse, RegFormsStateResponse
} from '../common/types';
import {
  actionPages as P,
  actionResults as R,
  actionSubjects as S,
  actionTypes as T,
  actionLogMessages as M
} from "../common/enums";

export class AtzmaiUsersApiService {
  static API_BASE = process.env.REACT_APP_API_SERVER;
  static URL = {
    LOGIN: '/auth/login',
    REQUEST_PASSWORD_RESET: '/auth/requestPasswordReset',
    APPROVE_PASSWORD_RESET: '/auth/approvePasswordReset',
    REGISTER: '/auth/registerNewUser',
    CHANGE_PASSWORD: '/auth/changePassword',
    GET_CODE: '/auth/loginByPhone',
    CONFIRM_CODE: '/auth/confirmOTC',
    LOGIN_AS_ADMIN: '/auth/loginAsAdmin',
    SEARCH_CUSTOMERS: '/customers/search',
    GET_CUSTOMER_BY_AGENT_ID: '/customers/getByAgentID',
    ORDER_INVOICE: '/invoiceOrders/orderInvoice',
    GET_CURRENT_MONTH_INVOICES: '/invoices/getCurrentMonthInvoices',
    CANCEL_INVOICE: '/invoices/cancelInvoice',
    GET_INVOICES_LIST: '/invoices/getList',
    GET_INVOICES_LIST_FOR_EXCEL: '/invoices/getListForExcel',
    GET_RECEIPTS_LIST: '/receipts/getList',
    GET_RECEIPTS_LIST_FOR_EXCEL: '/receipts/getListForExcel',
    GET_INVOICE_ORDERS_LIST: '/invoiceOrders/getList',
    GET_INVOICE_ORDERS_LIST_FOR_EXCEL: '/invoiceOrders/getListForExcel',
    GET_INVOICE_ORDER_DETAILS: '/invoiceOrders/getDetails',
    GET_CURRENT_USER: '/users/getCurrentUserDetails',
    GET_PAYMENT_PAGE: '/payments/getPaymentPage',
    GET_PAYMENT_CREDIT_CARD_PAGE: '/payments/getPaymentCreditCardPage',
    GET_PAYMENT_BIT_PAGE: '/payments/getPaymentBitPage',
    GET_PAYMENT_RECURRING_PAGE: '/payments/getRecurringPaymentLink',
    GET_PAYMENT_PAYPAL_PAGE: '/payments/getPaymentPaypalPage',
    CAPTURE_PAYPAL_PAYMENT: '/payments/capturePaypalPaymentOrder',
    INVITE_FRIEND: '/invites/inviteFriend',
    GET_CUSTOMER_OPTIONS: '/customers/getCustomerOptions',
    GET_INVOICE_OPTIONS: '/invoices/getInvoicesOptions',
    GET_PAYMENT_FORM_DETAILS: '/invoices/getPaymentFormDetailsByInvoice',
    GET_PAYMENT_FORM_DETAILS_BY_CUSTOMER_ID: 'customers/getPaymentFormDetailsByCustomer',
    GET_RECEIPT_CUSTOMER_OPTIONS: '/customers/getReceiptCustomerOptions',
    USER_DETAILS: '/users/updateCurrentUserDetails',
    GET_DASHBOARD_DATA: '/dashboard/getData',
    MARK_MESSAGE_AS_READ: '/dashboard/markMessagesAsRead',
    GET_TRANSFERS_LIST: '/transfers/getList',
    GET_REG_FORMS_STATE: '/regForms/getFormsData',
    GET_RECEIPT_DOCUMENT: '/receipts/getPDFDocument',
    GET_INVOICE_DOCUMENT: '/invoices/getPDFDocument',
    LOG_USER_ACTION: '/activityLog/create',
  };

  constructor(private token?: string) {
  }

  public setToken(token: string) {
    this.token = token;
  }

  public resetToken() {
    this.token = '';
  }

  public async login(login: string, password: string) {
    const config = this.getConfig();
    const response = await axios.post<LoginResponse,
      AxiosResponse<LoginResponse>,
      { login: string, password: string }>(
      AtzmaiUsersApiService.URL.LOGIN,
      {login, password},
      config
    );

    return response.data;
  }

  public async logUserAction(
    page: string,
    subject: string,
    action: string,
    payload: string,
    result: string
  ) {
    const config = this.getConfig();
    const response = await axios.post<
      LogUserActionResponse,
      AxiosResponse<LogUserActionResponse>,
      LogUserActionRequest
    >(
      AtzmaiUsersApiService.URL.LOG_USER_ACTION,
      {page, subject, action, payload, result},
      config
    );

    return response.data;
  }

  public async requestPasswordReset(email: string) {
    const config = this.getConfig();
    const response = await axios.post<ApiResponseFields,
      AxiosResponse<ApiResponseFields>,
      { email: string }>(
      AtzmaiUsersApiService.URL.REQUEST_PASSWORD_RESET,
      {email},
      config
    );

    return response.data;
  }

  public async approvePasswordReset(userId: string, password: string) {
    const config = this.getConfig();
    const response = await axios.post<ApiResponseFields,
      AxiosResponse<ApiResponseFields>,
      { userId: string, password: string }>(
      AtzmaiUsersApiService.URL.APPROVE_PASSWORD_RESET,
      {userId, password},
      config
    );

    return response.data;
  }

  public async register(
    email: string,
    phone: string,
    password: string,
    invite: string | null,
    affiliate: string | null
  ) {
    const config = this.getConfig();
    const response = await axios.post<
      RegisterResponse,
      AxiosResponse<RegisterResponse>,
      RegisterRequestBody>(
      AtzmaiUsersApiService.URL.REGISTER,
      {email, phone, password, invite, affiliate},
      config
    );

    return response.data;
  }

  public async changePassword(userId: string, oldPassword: string, newPassword: string) {
    const config = this.getConfig();
    const response = await axios.post<ApiResponseFields,
      AxiosResponse<ApiResponseFields>,
      { userId: string, oldPassword: string, newPassword: string }>(
      AtzmaiUsersApiService.URL.CHANGE_PASSWORD,
      {userId, oldPassword, newPassword},
      config
    );

    await this.logUserAction(
      P.AUTH,
      S.CHANGE_PASSWORD,
      T.GENERAL,
      M.CHANGE_PASSWORD(),
      response.data.errorCode === 0 ? R.SUCCESS : R.FAIL
    );

    return response.data;
  }

  public async getCode(phone: string) {
    const config = this.getConfig();
    const response = await axios.post<LoginResponse,
      AxiosResponse<LoginResponse>,
      { phone: string }>(
      AtzmaiUsersApiService.URL.GET_CODE,
      {phone},
      config
    );

    return response.data;
  }

  public async confirmCode(phone: string, otc: string) {
    const config = this.getConfig();
    const response = await axios.post<LoginResponse,
      AxiosResponse<LoginResponse>,
      { phone: string, otc: string }>(
      AtzmaiUsersApiService.URL.CONFIRM_CODE,
      {phone, otc},
      config
    );

    return response.data;
  }

  public async loginAsAdmin(login: string, password: string, targetUser: string) {
    const config = this.getConfig();
    const response = await axios.post<LoginResponse,
      AxiosResponse<LoginResponse>,
      { login: string, password: string, targetUser: string }>(
      AtzmaiUsersApiService.URL.LOGIN_AS_ADMIN,
      {login, password, targetUser},
      config
    );

    return response.data;
  }

  public async searchCustomers(text: ListCustomersBody['text']) {
    const config = this.getConfig();
    const response = await axios.post<ListCustomersResponse,
      AxiosResponse<ListCustomersResponse>,
      ListCustomersBody>(
      AtzmaiUsersApiService.URL.SEARCH_CUSTOMERS,
      {text},
      config
    );

    return response.data;
  }

  public async getCustomersByAgentID(agentID: number) {
    const config = this.getConfig();
    const response = await axios.post<ListCustomersByAgentIDResponse,
      AxiosResponse<ListCustomersByAgentIDResponse>,
      { agentID: number }>(
      AtzmaiUsersApiService.URL.GET_CUSTOMER_BY_AGENT_ID,
      {agentID},
      config
    );

    return response.data;
  }

  public async orderInvoice(invoice: InvoiceOrderBody) {
    const config = this.getConfig();
    const response = await axios.post<InvoiceOrderResponse,
      AxiosResponse<InvoiceOrderResponse>,
      InvoiceOrderBody>(
      AtzmaiUsersApiService.URL.ORDER_INVOICE,
      {...invoice},
      config
    );

    await this.logUserAction(
      P.ORDER_INVOICE,
      S.ORDER_INVOICE,
      T.INVOICES,
      M.ORDER_INVOICE({...invoice}),
      response.data.errorCode === 0 ? R.SUCCESS : R.FAIL
    );

    return response.data;
  }

  public async getCurrentMonthInvoices(idNumber: number) {
    const config = this.getConfig();
    const response = await axios.post<GetCurrentMonthInvoices,
      AxiosResponse<GetCurrentMonthInvoices>,
      { idNumber: number }>(
      AtzmaiUsersApiService.URL.GET_CURRENT_MONTH_INVOICES,
      {idNumber},
      config
    );

    return response.data.body;
  }

  public async cancelInvoice(
    invoiceNum: string,
    customerEmail: string,
    comments: string,
    datetime: Date
  ) {
    const config = this.getConfig();
    const response = await axios.post<CancelInvoiceResponse,
      AxiosResponse<CancelInvoiceResponse>,
      CancelInvoiceBody
    >(
      AtzmaiUsersApiService.URL.CANCEL_INVOICE,
      {invoiceNum, customerEmail, comments, datetime},
      config
    );

    await this.logUserAction(
      P.ORDER_INVOICE, // the same page when order invoice
      S.CANCEL_INVOICE,
      T.INVOICES,
      M.CANCEL_INVOICE({invoiceNum, customerEmail, comments, datetime}),
      response.data.errorCode === 0 ? R.SUCCESS : R.FAIL
    );

    return response.data;
  }

  public async getCurrentUser() {
    const config = this.getConfig();
    const response = await axios.get<CurrentUserResponse,
      AxiosResponse<CurrentUserResponse>>(
      AtzmaiUsersApiService.URL.GET_CURRENT_USER,
      config
    );

    return response.data;
  }

  public async getInvoicesList(
    page = 1,
    fromDate: Date | null,
    toDate: Date | null,
    customerID: string | null,
    invoiceID: string | null,
    status: number | null,
  ) {
    const config = this.getConfig();
    const data = {
      page,
      fromDate: fromDate ? format(fromDate, 'yyyy-MM-dd') : '',
      toDate: toDate ? format(toDate, 'yyyy-MM-dd') : '',
      customerID,
      invoiceID: invoiceID !== null ? parseInt(invoiceID) : null,
      status,
    };
    const response = await axios.post<GetInvoicesListResponse,
      AxiosResponse<GetInvoicesListResponse>>(
      AtzmaiUsersApiService.URL.GET_INVOICES_LIST,
      data,
      config
    );

    return response.data;
  }

  public async getInvoicesListForExcel(
    fromDate: Date | null,
    toDate: Date | null,
    customerID: string | null,
    invoiceID: string | null,
    status: number | null,
  ) {
    const config = this.getConfig();
    const data = {
      fromDate: fromDate ? format(fromDate, 'yyyy-MM-dd') : '',
      toDate: toDate ? format(toDate, 'yyyy-MM-dd') : '',
      customerID,
      invoiceID: invoiceID !== null ? parseInt(invoiceID) : null,
      status,
    };
    const response = await axios.post<GetInvoicesListForExcelResponse,
      AxiosResponse<GetInvoicesListForExcelResponse>>(
      AtzmaiUsersApiService.URL.GET_INVOICES_LIST_FOR_EXCEL,
      data,
      config
    );

    return response.data;
  }

  public async getInvoiceDocument(
    invoiceType: number,
    invoiceNum: number
  ) {
    const config = this.getConfig();
    const data = {invoiceType, invoiceNum};
    const response = await axios.post<GetInvoiceDocumentResponse,
      AxiosResponse<GetInvoiceDocumentResponse>>(
      AtzmaiUsersApiService.URL.GET_INVOICE_DOCUMENT,
      data,
      config
    );

    return response.data;
  }

  public async getReceiptsList(
    page = 1,
    fromDate: Date | null,
    toDate: Date | null,
    customerID: string | null,
  ) {
    const config = this.getConfig();
    const data = {
      page,
      fromDate: fromDate ? format(fromDate, 'yyyy-MM-dd') : '',
      toDate: toDate ? format(toDate, 'yyyy-MM-dd') : '',
      customerID,
    };
    const response = await axios.post<GetReceiptsListResponse,
      AxiosResponse<GetReceiptsListResponse>>(
      AtzmaiUsersApiService.URL.GET_RECEIPTS_LIST,
      data,
      config
    );

    return response.data;
  }

  public async getReceiptsListForExcel(
    fromDate: Date | null,
    toDate: Date | null,
    customerID: string | null,
  ) {
    const config = this.getConfig();
    const data = {
      fromDate: fromDate ? format(fromDate, 'yyyy-MM-dd') : '',
      toDate: toDate ? format(toDate, 'yyyy-MM-dd') : '',
      customerID
    };
    const response = await axios.post<GetReceiptsListForExcelResponse,
      AxiosResponse<GetReceiptsListForExcelResponse>>(
      AtzmaiUsersApiService.URL.GET_RECEIPTS_LIST_FOR_EXCEL,
      data,
      config
    );

    return response.data;
  }

  public async getReceiptDocument(receiptID: string) {
    const config = this.getConfig();
    const data = {receiptID};
    const response = await axios.post<GetReceiptDocumentResponse,
      AxiosResponse<GetReceiptDocumentResponse>>(
      AtzmaiUsersApiService.URL.GET_RECEIPT_DOCUMENT,
      data,
      config
    );

    return response.data;
  }

  public async getInvoiceOrdersList(
    page = 1,
    fromDate: Date | null,
    toDate: Date | null,
    text: string,
  ) {
    const config = this.getConfig();
    const data = {
      page,
      fromDate: fromDate ? format(fromDate, 'yyyy-MM-dd') : '',
      toDate: toDate ? format(toDate, 'yyyy-MM-dd') : '',
      text: text
    };
    const response = await axios.post<GetInvoiceOrdersListResponse,
      AxiosResponse<GetInvoiceOrdersListResponse>>(
      AtzmaiUsersApiService.URL.GET_INVOICE_ORDERS_LIST,
      data,
      config
    );

    return response.data;
  }

  public async getInvoiceOrdersListForExcel(
    fromDate: Date | null,
    toDate: Date | null,
    text: string,
  ) {
    const config = this.getConfig();
    const data = {
      fromDate: fromDate ? format(fromDate, 'yyyy-MM-dd') : '',
      toDate: toDate ? format(toDate, 'yyyy-MM-dd') : '',
      text: text
    };
    const response = await axios.post<GetInvoiceOrdersListForExcelResponse,
      AxiosResponse<GetInvoiceOrdersListForExcelResponse>>(
      AtzmaiUsersApiService.URL.GET_INVOICE_ORDERS_LIST_FOR_EXCEL,
      data,
      config
    );

    return response.data;
  }

  async getInvoiceOrderDetails(invoiceOrderID: string) {
    const config = this.getConfig();
    const data = {invoiceOrderID};
    const response = await axios.post<GetInvoiceOrderDetailsResponse,
      AxiosResponse<GetInvoiceOrderDetailsResponse>>(
      AtzmaiUsersApiService.URL.GET_INVOICE_ORDER_DETAILS,
      data,
      config
    );

    return response.data;
  }

  async getPaymentPage(
    items: { amount: number, description: string }[] = [],
    customerName: string,
    phone: string,
    email: string,
    paymentMethod: PaymentMethod,
    language: string,
    currency: Currency,
  ) {
    const config = this.getConfig();
    const data = {
      items,
      customerName,
      phone,
      email,
      paymentMethod,
      language,
      currency,
    };
    const response = await axios.post<GetPaymentPageResponse,
      AxiosResponse<GetPaymentPageResponse>>(
      AtzmaiUsersApiService.URL.GET_PAYMENT_PAGE,
      data,
      config
    );

    return response.data;
  }

  async getPaymentCreditCardPage(
    invoiceID: string,
    items: { amount: number; description: string }[] = [],
    customerID: string | null,
    customerName: string,
    phone: string,
    email: string,
    language: string,
    currency: Currency,
    priceIncludeVAT: boolean,
    payerIsCustomer: boolean,
    payerName: string | undefined,
    payerEmail: string | undefined,
    payerPhone: string | undefined
  ) {
    const config = this.getConfig();
    const data = {
      invoiceID,
      items,
      customerID,
      customerName,
      phone,
      email,
      language,
      currency,
      priceIncludeVAT,
      payerIsCustomer,
      payerName,
      payerEmail,
      payerPhone
    };
    const response = await axios.post<
      GetPaymentPageResponse,
      AxiosResponse<GetPaymentPageResponse>
    >(
      AtzmaiUsersApiService.URL.GET_PAYMENT_CREDIT_CARD_PAGE,
      data,
      config
    );

    await this.logUserAction(
      P.PAYMENT,
      S.GET_PAYMENT_CREDIT_CARD,
      T.PAYMENTS,
      M.CREATE_PAYMENT_CREDIT_CARD({...data}),
      response.data.errorCode === 0 ? R.SUCCESS : R.FAIL
    );

    return response.data;
  }

  async getPaymentBitPage(
    amount: number,
    description: string,
    customerName: string,
    phone: string,
    email: string,
    language: string,
    customerAddress: string,
    customerIdNumber: string,
  ) {
    const config = this.getConfig();
    const data = {
      amount,
      description,
      customerName,
      phone,
      email,
      language,
      customerAddress,
      customerIdNumber: customerIdNumber ? parseInt(customerIdNumber) : 0
    };
    const response = await axios.post<
      GetPaymentPageResponse,
      AxiosResponse<GetPaymentPageResponse>
    >(
      AtzmaiUsersApiService.URL.GET_PAYMENT_BIT_PAGE,
      data,
      config
    );

    await this.logUserAction(
      P.PAYMENT,
      S.GET_PAYMENT_BIT,
      T.PAYMENTS,
      M.CREATE_PAYMENT_BIT({...data}),
      response.data.errorCode === 0 ? R.SUCCESS : R.FAIL
    );

    return response.data;
  }

  async getPaymentRecurringPage(
    amount: number,
    description: string,
    recurringPeriod: number,
    paymentsCount: number,
    startDate: Date | string | null,
    customerName: string,
    email: string,
    phone: string,
  ) {
    const config = this.getConfig();
    const data = {
      amount,
      description,
      recurringPeriod,
      paymentsCount,
      startDate,
      customerName,
      email,
      phone,
    };
    const response = await axios.post<
      GetPaymentPageResponse,
      AxiosResponse<GetPaymentPageResponse>
    >(
      AtzmaiUsersApiService.URL.GET_PAYMENT_RECURRING_PAGE,
      data,
      config
    );

    await this.logUserAction(
      P.PAYMENT,
      S.GET_PAYMENT_RECURRING,
      T.PAYMENTS,
      M.CREATE_PAYMENT_RECURRING({...data}),
      response.data.errorCode === 0 ? R.SUCCESS : R.FAIL
    );

    return response.data;
  }

  async getPaymentPaypalPage(
    language: string,
    currency: Currency,
    description: string,
    amount: number,
    invoiceOrderID?: string,
    invoiceID?: string,
    phone?: string,
    email?: string,
    customerID?: string,
    customerName?: string,
  ) {
    const config = this.getConfig();
    const data = {
      language,
      currency,
      description,
      amount,
      invoiceOrderID,
      invoiceID,
      phone,
      email,
      customerID,
      customerName,
    };

    const response = await axios.post<
      GetPaymentPageResponse,
      AxiosResponse<GetPaymentPageResponse>
    >(
      AtzmaiUsersApiService.URL.GET_PAYMENT_PAYPAL_PAGE,
      data,
      config
    );

    await this.logUserAction(
      P.PAYMENT,
      S.GET_PAYMENT_PAYPAL,
      T.PAYMENTS,
      M.CREATE_PAYMENT_PAYPAL({...data}),
      response.data.errorCode === 0 ? R.SUCCESS : R.FAIL
    );

    return response.data;
  }

  public async capturePaypalPayment(id: string) {
    const config = this.getConfig();
    const data = {id};
    const response = await axios.post<CapturePaypalPaymentResponse,
      AxiosResponse<CapturePaypalPaymentResponse>>(
      AtzmaiUsersApiService.URL.CAPTURE_PAYPAL_PAYMENT,
      data,
      config
    );

    return response.data;
  }

  async inviteFriend(name: string, phone: string, language: string) {
    const config = this.getConfig();
    const data = {name, phone, language};
    const response = await axios.post<InviteFriendResponse,
      AxiosResponse<InviteFriendResponse>,
      InviteFriendRequestBody>(
      AtzmaiUsersApiService.URL.INVITE_FRIEND,
      data,
      config
    );

    await this.logUserAction(
      P.INVITE_FRIEND,
      S.INVITE_FRIEND,
      T.GENERAL,
      M.CREATE_INVITATION(name, phone),
      response.data.errorCode === 0 ? R.SUCCESS : R.FAIL
    );

    return response.data;
  }

  async getCustomerOptions(text: string) {
    const config = this.getConfig();
    const data = {text};
    const response = await axios.post<GetCustomerOptionsResponse,
      AxiosResponse<GetCustomerOptionsResponse>,
      GetCustomerOptionsRequestBody>(
      AtzmaiUsersApiService.URL.GET_CUSTOMER_OPTIONS,
      data,
      config
    );

    return response.data;
  }

  async getInvoiceOptions(text: string) {
    const config = this.getConfig();
    const data = {text};
    const response = await axios.post<GetCustomerOptionsResponse,
      AxiosResponse<GetCustomerOptionsResponse>,
      GetCustomerOptionsRequestBody>(
      AtzmaiUsersApiService.URL.GET_INVOICE_OPTIONS,
      data,
      config
    );

    return response.data;
  }

  async getReceiptCustomerOptions(text: string) {
    const config = this.getConfig();
    const data = {text};
    const response = await axios.post<GetCustomerOptionsResponse,
      AxiosResponse<GetCustomerOptionsResponse>,
      GetCustomerOptionsRequestBody>(
      AtzmaiUsersApiService.URL.GET_RECEIPT_CUSTOMER_OPTIONS,
      data,
      config
    );

    return response.data;
  }

  private getConfig() {
    const tokenHeader = this.token ? {token: this.token} : {};
    const config: AxiosRequestConfig = {
      baseURL: AtzmaiUsersApiService.API_BASE,
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        ...tokenHeader,
      }
    };

    return config;
  }

  public async changeUserDetails(details: UserDetailsBody) {
    const config = this.getConfig();
    const response = await axios.post<ApiResponseFields,
      AxiosResponse<ApiResponseFields>,
      UserDetailsBody>(
      AtzmaiUsersApiService.URL.USER_DETAILS,
      {...details},
      config
    );

    return response.data;
  }

  public async getDashboardData() {
    const config = this.getConfig();
    const response = await axios.get<DashboardDataResponse,
      AxiosResponse<DashboardDataResponse>>(
      AtzmaiUsersApiService.URL.GET_DASHBOARD_DATA,
      config
    );

    return response.data;
  }

  async markMessageAsRead(messageIDs: string[]) {
    const config = this.getConfig();
    const data = {messageIDs};
    const response = await axios.post<MarkMessageAsReadResponse,
      AxiosResponse<MarkMessageAsReadResponse>,
      MarkMessageAsReadBody>(
      AtzmaiUsersApiService.URL.MARK_MESSAGE_AS_READ,
      data,
      config
    );

    return response.data;
  }

  public async getPaymentFormDetailsByInvoice(invoiceID: string) {
    const config = this.getConfig();
    const data = {invoiceID};
    const response = await axios.post<GetPaymentFormDetailsByInvoiceResponse,
      AxiosResponse<GetPaymentFormDetailsByInvoiceResponse>,
      GetPaymentFormDetailsByInvoiceBody>(
      AtzmaiUsersApiService.URL.GET_PAYMENT_FORM_DETAILS,
      data,
      config
    );

    return response.data;
  }

  public async getPaymentFormDetailsByCustomerID(customerID: string) {
    const config = this.getConfig();
    const data = {customerID};
    const response = await axios.post<GetPaymentFormDetailsByCustomerIDResponse,
      AxiosResponse<GetPaymentFormDetailsByCustomerIDResponse>,
      GetPaymentFormDetailsByCustomerIDBody>(
      AtzmaiUsersApiService.URL.GET_PAYMENT_FORM_DETAILS_BY_CUSTOMER_ID,
      data,
      config
    );

    return response.data;
  }

  public async getTransfersList() {
    const config = this.getConfig();

    const response = await axios.post<GetTransfersListResponse,
      AxiosResponse<GetTransfersListResponse>>(
      AtzmaiUsersApiService.URL.GET_TRANSFERS_LIST,
      {},
      config
    );

    return response.data;
  }

  public async getRegFormsState(language: string) {
    const config = this.getConfig();
    const data = {language};
    const response = await axios.post<RegFormsStateResponse,
      AxiosResponse<RegFormsStateResponse>>(
      AtzmaiUsersApiService.URL.GET_REG_FORMS_STATE,
      data,
      config
    );

    return response.data;
  }
}
